import { i18n } from '../i18n.js';

/**
 * THE TICKET, as one object, built in one place.
 *
 * Two surfaces hand this ticket around and they must be the same physical
 * thing: the arrival screen an invited player lands on, and the invitation
 * dialog the inviter opens to send it. The dialog used to show a bare picture
 * of the artwork with none of the printing on it, so the object the sender saw
 * and the object the receiver saw were visibly different tickets.
 *
 * Everything documentary about it lives here — the wordmark, the issue date,
 * the issued-by line, the admission stamp and the serial — so neither surface
 * can drift from the other.
 *
 * Contract: tests/unit/ui/inviteTicketObject.test.js
 */

const ART_SRC = '/assets/images/invite-ticket.webp';

function uiText(key, fallback, replacements) {
  try {
    const value = i18n?.t?.(key, replacements);
    if (typeof value === 'string' && value && value !== key) return value;
  } catch (_) { /* i18n not ready */ }
  if (!replacements) return fallback;
  return Object.keys(replacements).reduce(
    (text, token) => text.replace(new RegExp(`{{${token}}}`, 'g'), replacements[token]),
    fallback,
  );
}

/** "AUGUST / 2026", in the reader's own language. */
function issueStamp() {
  try {
    const now = new Date();
    /* `getLanguage` is the accessor i18n exports; `current` never existed, so
       this stamp was English in every language. */
    const month = now.toLocaleString((i18n?.getLanguage?.() || 'en'), { month: 'long' }).toUpperCase();
    return `${month} / ${now.getFullYear()}`;
  } catch (_) {
    return '';
  }
}

function stamp(parent, className, label, value) {
  const block = document.createElement('div');
  block.className = `invite-ticket-stamp ${className}`;
  const top = document.createElement('span');
  top.className = 'invite-ticket-stamp-label';
  top.textContent = label;
  block.appendChild(top);
  if (value) {
    const bottom = document.createElement('span');
    bottom.className = 'invite-ticket-stamp-value';
    bottom.textContent = value;
    block.appendChild(bottom);
  }
  parent.appendChild(block);
  return block;
}

/**
 * A SHORT serial, never the whole code.
 *
 * The full invitation code is bearer-like — which is precisely why
 * `referral-link.js` strips it from the address bar so it cannot reach
 * screenshots, copied links or Referer headers. Printing all 32 characters
 * across the ticket would hand it straight back to the screenshot it is being
 * kept out of. The last six read as a serial, carry the documentary weight,
 * and are useless on their own.
 */
export function serialFor(code) {
  return String(code || '').slice(-6).toUpperCase();
}

/**
 * A STUB STAMP THAT OUTGROWS ITS COLUMN IS SET SMALLER, never wrapped.
 *
 * The stub holds two lines, a label over a value, between the perforation and
 * the stub's bottom edge. Wrapped, a long translation stacked four of them
 * there ("EINLASS FÜR / EINE PERSON / EIN PLATZ / RESERVIERT") and climbed
 * onto the perforation; German, Spanish, French and Portuguese all did, at
 * every screen size. The share card already fits its stub by font size
 * (game/utils/inviteShareImage.js); the ticket on screen now does the same,
 * measured in the browser, so the two printings agree.
 *
 * Layout widths only (`offsetWidth`, `scrollWidth`): the ticket may be in the
 * middle of its scaled entrance, and a transformed box would mis-measure.
 * Those are whole pixels, so a pixel of slack stands for the fraction they
 * drop: without it a label a tenth of a pixel too wide ("ADMITE UMA PESSOA")
 * read as fitting and wrapped anyway.
 *
 * @param {Element|null|undefined} ticket
 */
export function fitTicketStub(ticket) {
  const row = ticket?.querySelector?.('.invite-ticket-face-row--foot');
  const admit = /** @type {HTMLElement|null|undefined} */ (row?.querySelector('.invite-ticket-stamp--admit'));
  const serial = /** @type {HTMLElement|null|undefined} */ (row?.querySelector('.invite-ticket-stamp--serial'));
  if (!row || !admit || !serial || !row.isConnected) return;
  admit.style.removeProperty('font-size');
  const style = getComputedStyle(row);
  const room = row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
    - serial.offsetWidth - (parseFloat(style.columnGap) || 0);
  admit.style.whiteSpace = 'nowrap';
  const widest = Math.max(0, ...Array.from(admit.children, (line) => line.scrollWidth)) + 1;
  admit.style.removeProperty('white-space');
  if (!(room > 1) || widest <= room) return;
  const size = parseFloat(getComputedStyle(admit).fontSize);
  admit.style.fontSize = `${Math.floor((size * (room - 1) * 100) / widest) / 100}px`;
}

/**
 * Fit the stub now and whenever the ticket changes size. A ResizeObserver
 * reports after layout and before paint, so the first fit lands before the
 * ticket's first frame and a resize never shows a wrapped stub.
 *
 * @param {Element} ticket
 */
export function keepTicketStubFitted(ticket) {
  if (typeof ResizeObserver !== 'function') return;
  new ResizeObserver(() => fitTicketStub(ticket)).observe(ticket);
}

/**
 * Build the ticket.
 *
 * @param {object} [options]
 * @param {string|null} [options.code] the invitation code, for its serial.
 *   Optional because the ticket must still build without one -- a missing code
 *   prints an empty serial rather than throwing.
 * @param {string|null} [options.issuedBy] the name to print, if already known
 * @returns {{ ticket: HTMLElement, face: HTMLElement, art: HTMLImageElement,
 *             issuer: HTMLElement, admitStamp: HTMLElement, serialStamp: HTMLElement }}
 */
export function buildInviteTicket({ code, issuedBy = null } = {}) {
  /* NOT `.card`: that class carries the whole card stylesheet — the vmin
     --card-width/--card-height chain and its frame — which overrides the
     ticket's own geometry and renders the art tiny inside a card-shaped box. */
  const ticket = document.createElement('div');
  ticket.className = 'invite-ticket';
  ticket.dataset.inviteTicket = 'true';

  const art = document.createElement('img');
  art.className = 'invite-ticket-art';
  art.src = ART_SRC;
  art.alt = '';
  art.decoding = 'async';
  /* The ticket IS the moment, so it is the one image on this screen worth
     fetching ahead of everything else. */
  art.setAttribute('fetchpriority', 'high');
  ticket.appendChild(art);

  /* THE DOCUMENTARY FACE.
     Studied from cloudflare.pay, whose claim card prints five pieces of
     letterspaced monospace in its corners at 9px, where nobody reads them.
     That is the point: they are the visual grammar of passports, boarding
     passes and share certificates, and they say "an instrument was issued,
     with a serial and a date" faster than a sentence can.

     Every label is TRUE. The ticket really does admit one player, the code
     really is its serial, and the date really is when it was issued. No "one
     of one", because an invite code is not unique to a person and printing
     that it is would be a fabricated state. */
  const face = document.createElement('div');
  face.className = 'invite-ticket-face';
  face.setAttribute('aria-hidden', 'true');

  const head = document.createElement('div');
  head.className = 'invite-ticket-face-row invite-ticket-face-row--head';
  stamp(head, 'invite-ticket-stamp--brand', 'MOONFALL', '');
  stamp(head, 'invite-ticket-stamp--date', issueStamp(), '');
  face.appendChild(head);

  /* The whole bottom block — issuer line then the admit/serial row — as ONE
     flow column. The issuer used to be absolutely positioned at a hand-tuned
     `bottom`, which collided with the admit row the moment that row gained
     padding: "ADMIT ONE" and "ISSUED BY <name>" printed on top of each other
     and neither was readable. Two lines that must never overlap belong in the
     same flow, not in two coordinate systems. */
  const footer = document.createElement('div');
  footer.className = 'invite-ticket-face-foot';

  const foot = document.createElement('div');
  foot.className = 'invite-ticket-face-row invite-ticket-face-row--foot';
  const admitStamp = stamp(
    foot, 'invite-ticket-stamp--admit',
    uiText('invite.ticketAdmit', 'ADMIT ONE'),
    uiText('invite.ticketSeat', 'ONE SEAT HELD'),
  );
  const serialStamp = stamp(
    foot, 'invite-ticket-stamp--serial',
    uiText('invite.ticketSerial', 'NO.'),
    serialFor(code),
  );

  const issuer = document.createElement('div');
  issuer.className = 'invite-ticket-issuer';
  issuer.setAttribute('aria-hidden', 'true');
  if (issuedBy) {
    issuer.dataset.inviter = String(issuedBy).toUpperCase();
    issuer.textContent = uiText('invite.ticketIssuedBy', 'ISSUED BY {{name}}', {
      name: issuer.dataset.inviter,
    });
    issuer.classList.add('invite-ticket-issuer--filled');
  }

  /* Issuer above the admit row, both in the footer column. The issuer keeps its
     line box while empty, so a name landing later changes no geometry. */
  footer.append(issuer, foot);
  face.appendChild(footer);
  ticket.appendChild(face);

  /* The tilt engine drives this; without it the ticket rotates but never
     catches the light, which is most of what makes it read as a real card. */
  const glare = document.createElement('span');
  glare.className = 'invite-ticket-glare';
  glare.setAttribute('aria-hidden', 'true');
  ticket.appendChild(glare);

  keepTicketStubFitted(ticket);

  return { ticket, face, art, issuer, admitStamp, serialStamp };
}

/** Re-print the localized stamps in place, for a language change. */
export function relocalizeInviteTicket(root) {
  if (!root) return;
  const set = (selector, label, value) => {
    const block = root.querySelector(selector);
    if (!block) return;
    const top = block.querySelector('.invite-ticket-stamp-label');
    const bottom = block.querySelector('.invite-ticket-stamp-value');
    if (top && label != null) top.textContent = label;
    if (bottom && value != null) bottom.textContent = value;
  };
  set('.invite-ticket-stamp--date', issueStamp(), null);
  set('.invite-ticket-stamp--admit',
    uiText('invite.ticketAdmit', 'ADMIT ONE'),
    uiText('invite.ticketSeat', 'ONE SEAT HELD'));
  set('.invite-ticket-stamp--serial', uiText('invite.ticketSerial', 'NO.'), null);
  const issuer = root.querySelector('.invite-ticket-issuer');
  if (issuer?.dataset?.inviter) {
    issuer.textContent = uiText('invite.ticketIssuedBy', 'ISSUED BY {{name}}', {
      name: issuer.dataset.inviter,
    });
  }
  /* New words, same ticket size: the observer will not fire, so fit here. */
  fitTicketStub(root.matches?.('.invite-ticket') ? root : root.querySelector('.invite-ticket'));
}

export const INVITE_TICKET_ART_SRC = ART_SRC;

/* ==========================================================================
   A TICKET IS SHOWN WHOLE, OR NOT YET.

   Three surfaces present this object -- the invited player's arrival screen,
   the voucher link's arrival screen and the inviter's own invitation dialog --
   and the same defect was found on each in turn: the face printed itself onto
   the empty space where the artwork was going to be, and the 326KB of
   filigree arrived underneath it a second later. "it first loads the text and
   then it is loading the image ... it breaks the illusion of a physical object
   and it also looks buggy, for one second the player thinks it's a bug." A
   printed thing cannot be watched acquiring its own paper.

   The two arrival screens each carried a private copy of the wait and the
   dialog carried none, which is how the dialog was the one to slip. The wait
   lives here now, once, and every surface that builds a ticket uses it:

   - `warmInviteTicketArt` starts the download early -- at boot for an arrival,
     on intent and at click for the dialog -- so the wait below is normally a
     cached decode that costs a frame, not a download.
   - `whenInviteTicketWhole` resolves once the ticket's own <img> has DECODED
     (not merely loaded: a large WebP decodes on the reveal frame otherwise),
     or at a hard cap. A gate that can fail to open has deleted the feature it
     was warming up, so a broken or stalled image releases the surface too, and
     whatever the surface then shows is the truthful state rather than a
     substitute.
   ========================================================================== */

/* --------------------------------------------------------------------------
   AN ARRIVAL SEES ONE SCREEN, NOT TWO.

   Confirming a link's code costs a round-trip and the app does not wait for
   it, so the boot veil dropped, "Sign in to Play" (or the guest tutorial's
   mulligan) painted, and the ticket landed on top of it a beat later -- a
   screen that was never meant for this player, shown first. The invitation
   arrival fixed that by asking `main.js` to keep the veil up until its ticket
   was on screen; the voucher arrival never did, so a `/play?redeem=` link
   still booted the app in full view and covered it afterwards.

   Both arrivals hold the veil through this one registry. `main.js` reads the
   document flag and waits for `moonfall:invitesettled`; the flag clears and the
   event fires when the LAST holder releases, so two links in one visit cannot
   drop the veil on each other. Every hold carries its own hard cap, because
   this holds the WHOLE APP behind a loading screen and must never be able to
   stick.
   -------------------------------------------------------------------------- */
const veilHolders = new Map();

/**
 * @param {string} owner
 * @param {{ capMs?: number }} [options]
 */
export function holdBootVeilForTicket(owner, { capMs = 10000 } = {}) {
  if (!owner || veilHolders.has(owner)) return;
  try {
    if (!document.documentElement) return;
    document.documentElement.dataset.invitePending = '1';
  } catch (_) { return; }
  const timer = setTimeout(() => releaseBootVeilForTicket(owner), capMs);
  veilHolders.set(owner, timer);
}

export function releaseBootVeilForTicket(owner) {
  if (!veilHolders.has(owner)) return;
  clearTimeout(veilHolders.get(owner));
  veilHolders.delete(owner);
  if (veilHolders.size) return;
  try { delete document.documentElement.dataset.invitePending; } catch (_) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent('moonfall:invitesettled')); } catch (_) { /* ignore */ }
}

/** One warm per source for the life of the page; the promise never rejects. */
const artWarms = new Map();

/**
 * @param {string} [src]
 * @param {{ priority?: 'high'|'low'|'auto' }} [options]
 * @returns {Promise<void>}
 */
export function warmInviteTicketArt(src = ART_SRC, { priority = 'high' } = {}) {
  const known = artWarms.get(src);
  if (known) return known;
  const warm = new Promise((resolve) => {
    try {
      const img = new Image();
      img.decoding = 'async';
      try { img.fetchPriority = priority; } catch (_) { /* not everywhere */ }
      img.addEventListener('load', () => resolve(), { once: true });
      img.addEventListener('error', () => resolve(), { once: true });
      img.src = src;
      if (img.complete) resolve();
    } catch (_) { resolve(); }
  });
  artWarms.set(src, warm);
  return warm;
}

/**
 * Resolve when `art` can be painted complete, or at `capMs`. Never rejects.
 *
 * `decode()` rejects on a broken image; that is a reason to reveal the
 * surface (its truthful error well), not to hold it, so the rejection is
 * swallowed. Engines without `decode()` fall back to the load/error events.
 *
 * @param {HTMLImageElement|null|undefined} art
 * @param {{ capMs?: number, extra?: Array<Promise<unknown>|null|undefined> }} [options]
 * @returns {Promise<void>}
 */
export function whenInviteTicketWhole(art, { capMs, extra = [] } = {}) {
  const waits = [];
  if (art && typeof art.decode === 'function') {
    waits.push(art.decode().catch(() => {}));
  } else if (art && !art.complete) {
    waits.push(new Promise((resolve) => {
      art.addEventListener('load', resolve, { once: true });
      art.addEventListener('error', resolve, { once: true });
    }));
  }
  for (const wait of extra) {
    if (wait && typeof wait.then === 'function') waits.push(wait.catch(() => {}));
  }
  if (!waits.length) return Promise.resolve();
  const settled = Promise.all(waits).then(() => undefined);
  if (!(capMs > 0)) return settled;
  return Promise.race([
    settled,
    new Promise((resolve) => { setTimeout(resolve, capMs); }),
  ]);
}
